import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { CarrierBay } from '@components/CarrierBay';
import { Collider } from '@components/Collider';
import { ViewportEntry } from '@components/ViewportEntry';
import { Tag } from '@components/Tag';
import { EntityFactory } from '@entities/EntityFactory';
import { WeaponUtils } from '@utils/WeaponUtils';
import { Effects } from '@/effects/Effects';

export class CarrierSystem extends System {
  private canvas!: HTMLCanvasElement;

  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }

  update(_deltaTime: number): void {
    const currentTime = Date.now();
    const hasPlayers = this.getEntitiesWithComponents(Tag).some(e => e.getComponent(Tag)!.value === 'player');
    if (!hasPlayers) return;

    for (const carrier of this.getEntitiesWithComponents(Position, Velocity, CarrierBay, Tag)) {
      if (carrier.getComponent(Tag)!.value !== 'enemy') continue;

      const position = carrier.getComponent(Position)!;
      const velocity = carrier.getComponent(Velocity)!;
      const bay = carrier.getComponent(CarrierBay)!;

      if (currentTime - bay.lastLaunch < bay.cooldown) continue;
      if (!WeaponUtils.isInViewport(position, this.canvas)) continue;

      bay.launchedIds = bay.launchedIds.filter(id => {
        const fighter = this.world.getEntity(id);
        return fighter !== undefined && !this.world.isPendingRemoval(fighter);
      });

      const launchCount = Math.min(bay.squadronSize, bay.maxActive - bay.launchedIds.length);
      if (launchCount <= 0) continue;

      const radius = carrier.getComponent(Collider)?.radius ?? 20;
      for (let i = 0; i < launchCount; i++) {
        // Alternate launches between port and starboard bays
        const side = i % 2 === 0 ? 1 : -1;
        const launchAngle = velocity.angle + (Math.PI / 2) * side;
        const x = position.x + Math.cos(launchAngle) * radius;
        const y = position.y + Math.sin(launchAngle) * radius;

        const swarmer = EntityFactory.createSwarmer(x, y);
        const swarmerVelocity = swarmer.getComponent(Velocity)!;
        swarmerVelocity.angle = launchAngle;
        swarmer.getComponent(ViewportEntry)!.hasEntered = true;

        this.world.addEntity(swarmer);
        bay.launchedIds.push(swarmer.id);
        Effects.muzzleFlash(this.world, x, y, launchAngle, '#ccff00', 1.5);
      }

      bay.lastLaunch = currentTime;
    }
  }
}
